import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import AppDialog, { ModalActions } from '@/components/CustomModal'
import { toast } from 'sonner'
import { MessageSquare } from 'lucide-react'
import {
  useMessagePacksQuery,
  useRequestPackMutation,
  type MessagePack,
} from '@/hooks/useMessagePacks'

function money(n: number) {
  return `${new Intl.NumberFormat('fr-FR').format(n)} XOF`
}

export function MessagePacksCard() {
  const { data: packs, isLoading } = useMessagePacksQuery()
  const requestMut = useRequestPackMutation()
  const [selected, setSelected] = useState<MessagePack | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [successInfo, setSuccessInfo] = useState<{
    instructions: string
    pack: MessagePack
  } | null>(null)

  const handleRequest = async () => {
    if (!selected) return
    const result = await requestMut.mutateAsync(selected.code)
    setConfirmOpen(false)
    setSuccessInfo({ instructions: result.instructions, pack: result.pack })
    toast.success(`Demande envoyée pour ${result.pack.name}`)
  }

  if (isLoading) return null

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2 text-base'>
            <MessageSquare className='h-4 w-4' />
            Packs WhatsApp
          </CardTitle>
        </CardHeader>
        <CardContent className='space-y-3'>
          <p className='text-sm text-muted-foreground'>
            Achetez un pack pour continuer à envoyer des relances WhatsApp après épuisement de votre quota.
          </p>

          <div className='grid gap-3 sm:grid-cols-3'>
            {(packs ?? []).map((pack) => (
              <div
                key={pack.code}
                className='rounded-lg border p-4 space-y-2 hover:border-primary transition-colors cursor-pointer'
                onClick={() => {
                  setSelected(pack)
                  setConfirmOpen(true)
                }}
              >
                <div className='font-semibold text-sm'>{pack.name}</div>
                <div className='text-2xl font-bold tabular-nums'>
                  {pack.messages}
                  <span className='text-sm font-normal text-muted-foreground ml-1'>msg</span>
                </div>
                <div className='text-sm font-medium text-primary'>
                  {money(pack.priceCfa)}
                </div>
                <Button
                  size='sm'
                  variant='outline'
                  className='w-full'
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelected(pack)
                    setConfirmOpen(true)
                  }}
                >
                  Demander
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Dialog confirmation demande */}
      <AppDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title='Confirmer la demande'
        description={
          selected
            ? `${selected.name} — ${selected.messages} messages pour ${money(selected.priceCfa)}`
            : ''
        }
        footer={
          <ModalActions
            confirmLabel='Envoyer la demande'
            confirmLoading={requestMut.isPending}
            onConfirm={handleRequest}
          />
        }
      >
        <div className='px-4 py-2 text-sm text-muted-foreground'>
          Votre demande sera traitée manuellement. Vous recevrez vos messages après confirmation du paiement.
        </div>
      </AppDialog>

      {/* Dialog instructions paiement */}
      <AppDialog
        open={!!successInfo}
        onOpenChange={(v) => { if (!v) setSuccessInfo(null) }}
        title='Demande envoyée'
        description={successInfo?.pack.name ?? ''}
        footer={
          <ModalActions
            confirmLabel='Fermer'
            onConfirm={() => setSuccessInfo(null)}
          />
        }
      >
        <div className='px-4 py-3 space-y-2'>
          <p className='text-sm'>{successInfo?.instructions}</p>
          <p className='text-xs text-muted-foreground'>
            Votre quota sera mis à jour dès que le paiement sera confirmé par notre équipe.
          </p>
        </div>
      </AppDialog>
    </>
  )
}
